import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Badge } from "@/components/ui/badge";
import { ClockCounterClockwise, Spinner, CaretDown, CaretUp, MapPin, Newspaper, X, ArrowSquareOut, Planet } from "@phosphor-icons/react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceDot } from "recharts";
import AstrologyLiquidity from "./AstrologyLiquidity";

const ASSETS = [
  { id: "bitcoin", label: "Bitcoin", short: "BTC" },
  { id: "ethereum", label: "Ethereum", short: "ETH" },
  { id: "solana", label: "Solana", short: "SOL" },
  { id: "SPX", label: "S&P 500", short: "SPX" },
  { id: "NVDA", label: "Nvidia", short: "NVDA" },
  { id: "DXY", label: "Dollar Index", short: "DXY" },
  { id: "TNX", label: "10Y Treasury", short: "TNX" },
  { id: "gold", label: "Gold", short: "GOLD" },
];

const PERIODS = [
  { id: "1y", label: "1Y" },
  { id: "3y", label: "3Y" },
  { id: "5y", label: "5Y" },
  { id: "max", label: "MAX" },
];

const EVENTS = {
  bitcoin: [
    { date: "2017-12-17", title: "BTC Hits $20K", desc: "Bitcoin reached its first major all-time high of ~$19,783 during the ICO-fueled bull run.", impact: "bullish", search: "Bitcoin 20000 all time high" },
    { date: "2018-12-15", title: "Crypto Winter Bottom", desc: "BTC bottomed near $3,200 after a year-long bear market. 84% drawdown from ATH.", impact: "bearish", search: "Bitcoin crash 3200 crypto winter" },
    { date: "2020-03-12", title: "COVID Black Thursday", desc: "BTC crashed 40% in a single day to ~$3,800 as global markets panicked over COVID-19.", impact: "bearish", search: "Bitcoin crash COVID Black Thursday" },
    { date: "2020-05-11", title: "3rd Bitcoin Halving", desc: "Block reward cut from 12.5 to 6.25 BTC. Historically triggers bull runs within 12-18 months.", impact: "bullish", search: "Bitcoin halving 2020" },
    { date: "2021-02-08", title: "Tesla Buys $1.5B BTC", desc: "Elon Musk announced Tesla purchased $1.5B in Bitcoin, sending BTC above $44K.", impact: "bullish", search: "Tesla Bitcoin purchase 1.5 billion" },
    { date: "2021-04-14", title: "Coinbase IPO", desc: "Coinbase went public at $381/share. BTC hit ATH of $64,800 the same day.", impact: "bullish", search: "Coinbase IPO Bitcoin" },
    { date: "2021-05-19", title: "China Mining Ban Crash", desc: "China banned crypto mining. BTC dropped 30% in hours to ~$30K.", impact: "bearish", search: "China crypto mining ban Bitcoin crash" },
    { date: "2021-09-07", title: "El Salvador Adopts BTC", desc: "El Salvador became the first country to adopt Bitcoin as legal tender.", impact: "bullish", search: "El Salvador Bitcoin legal tender" },
    { date: "2021-11-10", title: "BTC ATH $69K", desc: "Bitcoin reached $68,789, driven by ETF anticipation and institutional adoption.", impact: "bullish", search: "Bitcoin all time high 69000" },
    { date: "2022-05-09", title: "LUNA/UST Collapse", desc: "Terra LUNA and UST de-pegged, wiping $40B. BTC fell from $35K to $26K.", impact: "bearish", search: "Terra Luna UST collapse Bitcoin" },
    { date: "2022-06-13", title: "Celsius/3AC Collapse", desc: "Celsius froze withdrawals, Three Arrows Capital defaulted. BTC fell to $17.6K.", impact: "bearish", search: "Celsius Three Arrows Capital Bitcoin" },
    { date: "2022-11-08", title: "FTX Collapse", desc: "FTX filed for bankruptcy. BTC crashed to $15,500 — the cycle low.", impact: "bearish", search: "FTX bankruptcy Bitcoin crash" },
    { date: "2024-01-10", title: "Spot BTC ETF Approved", desc: "SEC approved 11 spot Bitcoin ETFs. Institutional money flooded in.", impact: "bullish", search: "Bitcoin spot ETF SEC approved" },
    { date: "2024-04-20", title: "4th Bitcoin Halving", desc: "Block reward cut from 6.25 to 3.125 BTC. Supply shock began.", impact: "bullish", search: "Bitcoin halving 2024" },
    { date: "2024-12-05", title: "BTC Breaks $100K", desc: "Bitcoin surpassed $100,000 for the first time, fueled by ETF inflows and halving supply shock.", impact: "bullish", search: "Bitcoin 100000 all time high" },
    { date: "2025-01-20", title: "Trump Inauguration Rally", desc: "Bitcoin surged past $105K on pro-crypto sentiment following Trump's inauguration and executive orders on digital assets.", impact: "bullish", search: "Trump inauguration Bitcoin crypto executive order" },
    { date: "2025-03-02", title: "US Strategic BTC Reserve", desc: "Trump signed executive order establishing a US Strategic Bitcoin Reserve using seized BTC.", impact: "bullish", search: "US strategic Bitcoin reserve executive order" },
    { date: "2025-04-02", title: "Tariff Shock Crash", desc: "Trump's sweeping 'Liberation Day' tariffs triggered a global risk-off sell-off. BTC dropped from $88K to $74K.", impact: "bearish", search: "Trump tariffs Liberation Day Bitcoin crash" },
  ],
  ethereum: [
    { date: "2017-06-12", title: "ETH Hits $400 (ICO Boom)", desc: "Ethereum surged on massive ICO demand, becoming the platform for token launches.", impact: "bullish", search: "Ethereum ICO boom 400" },
    { date: "2018-01-13", title: "ETH ATH $1,430", desc: "Ethereum hit its first major ATH during the 2017-2018 crypto bubble peak.", impact: "bullish", search: "Ethereum all time high 1430" },
    { date: "2020-03-12", title: "COVID Crash", desc: "ETH crashed to $86 during COVID Black Thursday panic selling.", impact: "bearish", search: "Ethereum crash COVID" },
    { date: "2020-12-01", title: "ETH 2.0 Beacon Chain", desc: "Ethereum's proof-of-stake Beacon Chain launched, starting the transition from PoW.", impact: "bullish", search: "Ethereum 2.0 Beacon Chain launch" },
    { date: "2021-05-04", title: "ETH Breaks $3,000", desc: "DeFi and NFT mania drove ETH above $3,000 for the first time.", impact: "bullish", search: "Ethereum 3000 DeFi NFT" },
    { date: "2021-11-10", title: "ETH ATH $4,878", desc: "Ethereum hit all-time high fueled by DeFi TVL, NFTs, and Layer 2 growth.", impact: "bullish", search: "Ethereum all time high 4878" },
    { date: "2022-06-18", title: "ETH Below $900", desc: "ETH crashed to $880 amid Celsius, 3AC, and broader crypto contagion.", impact: "bearish", search: "Ethereum crash below 900" },
    { date: "2022-09-15", title: "The Merge (PoS)", desc: "Ethereum transitioned from Proof of Work to Proof of Stake, cutting energy use 99.95%.", impact: "bullish", search: "Ethereum Merge Proof of Stake" },
    { date: "2024-05-23", title: "Spot ETH ETF Approved", desc: "SEC approved spot Ethereum ETFs, legitimizing ETH as institutional-grade asset.", impact: "bullish", search: "Ethereum spot ETF SEC approved" },
    { date: "2025-03-13", title: "Pectra Upgrade", desc: "Ethereum's Pectra upgrade improved staking, blobs, and account abstraction.", impact: "bullish", search: "Ethereum Pectra upgrade" },
    { date: "2025-04-02", title: "Tariff Sell-Off", desc: "ETH dropped below $1,800 as Trump tariff panic hit crypto markets hard.", impact: "bearish", search: "Ethereum tariff crash 2025" },
  ],
  solana: [
    { date: "2021-09-09", title: "SOL Hits $213 ATH", desc: "Solana surged to $213, becoming a top-5 crypto by market cap amid DeFi growth.", impact: "bullish", search: "Solana all time high 213" },
    { date: "2021-09-14", title: "First Major Outage", desc: "Solana network went offline for 17 hours due to bot-driven resource exhaustion.", impact: "bearish", search: "Solana network outage" },
    { date: "2022-11-08", title: "FTX Collapse Impact", desc: "SOL crashed from $36 to $8 due to heavy FTX/Alameda exposure.", impact: "bearish", search: "Solana FTX crash" },
    { date: "2023-10-15", title: "SOL Recovery Begins", desc: "Solana began its recovery, climbing from $22 on renewed developer activity.", impact: "bullish", search: "Solana recovery 2023" },
    { date: "2024-03-18", title: "Meme Coin Supercycle", desc: "pump.fun and Jupiter drove massive Solana activity. SOL surged past $180.", impact: "bullish", search: "Solana meme coin pump.fun" },
    { date: "2024-11-22", title: "SOL ATH $263", desc: "Solana broke all-time highs at $263, driven by institutional interest and DeFi growth.", impact: "bullish", search: "Solana all time high 263" },
    { date: "2025-01-19", title: "Trump OFFICIAL Meme Coin", desc: "Trump launched $TRUMP meme coin on Solana, sending SOL past $270 briefly.", impact: "bullish", search: "Trump meme coin Solana TRUMP" },
    { date: "2025-04-07", title: "SOL Drops Below $100", desc: "Solana fell below $100 amid macro tariff fears and broad crypto sell-off.", impact: "bearish", search: "Solana price crash 2025 tariff" },
  ],
  SPX: [
    { date: "2018-12-24", title: "Christmas Eve Crash", desc: "S&P 500 dropped 20% from highs on Fed rate hike fears and trade war uncertainty.", impact: "bearish", search: "S&P 500 Christmas Eve crash 2018" },
    { date: "2020-02-20", title: "COVID Crash Begins", desc: "S&P 500 began a 34% crash over 23 trading days — the fastest bear market in history.", impact: "bearish", search: "S&P 500 COVID crash February 2020" },
    { date: "2020-03-23", title: "COVID Bottom", desc: "S&P 500 bottomed at 2,237. The Fed announced unlimited QE the same day.", impact: "bearish", search: "S&P 500 bottom Fed unlimited QE" },
    { date: "2020-08-18", title: "SPX Full Recovery", desc: "S&P 500 fully recovered COVID losses and hit new highs, powered by tech stocks.", impact: "bullish", search: "S&P 500 recovery 2020 all time high" },
    { date: "2021-01-27", title: "GameStop Squeeze", desc: "Reddit-driven short squeeze of GME caused market-wide volatility.", impact: "bearish", search: "GameStop short squeeze Reddit S&P" },
    { date: "2022-01-03", title: "SPX ATH Before Rate Hikes", desc: "S&P hit 4,796 before the Fed began its aggressive rate hiking cycle.", impact: "bullish", search: "S&P 500 all time high January 2022" },
    { date: "2022-06-13", title: "Bear Market Confirmed", desc: "S&P 500 closed 20% below its January high, officially entering bear market.", impact: "bearish", search: "S&P 500 bear market 2022" },
    { date: "2022-10-12", title: "2022 Bear Market Low", desc: "S&P 500 bottomed at 3,577 on sticky inflation fears.", impact: "bearish", search: "S&P 500 bottom October 2022" },
    { date: "2023-01-06", title: "AI Rally Starts", desc: "ChatGPT hype sparked an AI-driven tech rally that dominated 2023.", impact: "bullish", search: "AI rally ChatGPT S&P 500 2023" },
    { date: "2024-01-19", title: "SPX Breaks ATH", desc: "S&P 500 broke above its January 2022 ATH, entering new all-time high territory.", impact: "bullish", search: "S&P 500 all time high January 2024" },
    { date: "2024-07-16", title: "SPX 5,667 ATH", desc: "S&P 500 hit 5,667, driven by AI spending, strong earnings, and rate cut expectations.", impact: "bullish", search: "S&P 500 all time high July 2024" },
    { date: "2025-02-19", title: "SPX ATH 6,144", desc: "S&P 500 reached new all-time high of 6,144 on strong tech earnings and AI optimism.", impact: "bullish", search: "S&P 500 all time high February 2025" },
    { date: "2025-04-03", title: "Tariff Crash -5%", desc: "S&P 500 plunged 5% in a single day after Trump's sweeping tariff announcement. Worst day since 2020.", impact: "bearish", search: "S&P 500 tariff crash April 2025" },
    { date: "2025-04-07", title: "Bear Market Fears", desc: "S&P 500 dropped another 3%, nearing bear market territory. Global markets in turmoil over trade war escalation.", impact: "bearish", search: "S&P 500 bear market tariff trade war April 2025" },
  ],
  NVDA: [
    { date: "2020-03-18", title: "COVID Low $49", desc: "Nvidia bottomed near $49 during the COVID crash.", impact: "bearish", search: "Nvidia stock COVID crash" },
    { date: "2022-10-13", title: "Crypto/Gaming Bust Low", desc: "NVDA bottomed at $108 after crypto mining demand collapsed.", impact: "bearish", search: "Nvidia stock bottom 2022" },
    { date: "2023-05-24", title: "Earnings Shock: AI Boom", desc: "Nvidia reported 19% revenue beat and guided 50% above consensus. Stock jumped 24%.", impact: "bullish", search: "Nvidia earnings AI boom May 2023" },
    { date: "2024-02-22", title: "NVDA Earnings Blowout", desc: "Revenue tripled YoY to $22.1B. Stock surged past $800 on AI data center demand.", impact: "bullish", search: "Nvidia earnings revenue triple 2024" },
    { date: "2024-06-10", title: "10:1 Stock Split", desc: "Nvidia completed a 10-for-1 stock split. Briefly became world's most valuable company.", impact: "bullish", search: "Nvidia stock split 10 for 1" },
    { date: "2024-06-20", title: "NVDA Hits $140 ATH", desc: "Nvidia reached $140 (split-adjusted), becoming the most valuable company globally.", impact: "bullish", search: "Nvidia all time high most valuable company" },
    { date: "2025-01-27", title: "DeepSeek Scare", desc: "Chinese AI startup DeepSeek rattled NVDA -17% on fears of cheaper AI training.", impact: "bearish", search: "Nvidia DeepSeek crash January 2025" },
    { date: "2025-03-10", title: "NVDA Drops Below $100", desc: "Nvidia fell below $100 on broader tech sell-off and AI spending concerns.", impact: "bearish", search: "Nvidia stock drop below 100 March 2025" },
    { date: "2025-04-03", title: "Tariff Hit on Chips", desc: "Semiconductor tariff fears slammed NVDA -8% as China trade war escalated.", impact: "bearish", search: "Nvidia tariff semiconductor chip ban April 2025" },
  ],
  DXY: [
    { date: "2020-03-20", title: "COVID Dollar Spike", desc: "Dollar surged to 102.8 as investors fled to USD safety during COVID panic.", impact: "bullish", search: "US dollar index COVID rally" },
    { date: "2021-01-06", title: "DXY Bottom ~89.2", desc: "Dollar hit multi-year lows on massive Fed money printing and zero rates.", impact: "bearish", search: "US dollar index low 2021" },
    { date: "2022-09-28", title: "DXY Peak 114.8", desc: "Dollar hit 20-year highs as the Fed aggressively raised rates.", impact: "bullish", search: "US dollar index 20 year high 2022" },
    { date: "2023-07-14", title: "DXY Dropped to 99.5", desc: "Dollar weakened as inflation cooled and rate cut expectations built.", impact: "bearish", search: "US dollar index falls 2023" },
    { date: "2024-09-27", title: "DXY Low 100.2", desc: "Dollar weakened on Fed rate cut expectations after September FOMC.", impact: "bearish", search: "US dollar index Fed rate cut September 2024" },
    { date: "2025-04-03", title: "DXY Drops on Tariffs", desc: "Paradoxically, the dollar weakened after tariff announcement as markets priced in US recession risk.", impact: "bearish", search: "US dollar index tariff drop April 2025" },
  ],
  TNX: [
    { date: "2020-03-09", title: "Yield Crashes to 0.5%", desc: "10-Year yield hit historic low of 0.5% as the Fed cut rates to zero during COVID.", impact: "bearish", search: "10 year treasury yield COVID low" },
    { date: "2020-08-04", title: "All-Time Low 0.52%", desc: "Yields hit absolute bottom as Fed maintained emergency zero-rate policy.", impact: "bearish", search: "10 year treasury yield all time low" },
    { date: "2022-03-16", title: "Fed Rate Hikes Begin", desc: "Fed raised rates 25bp — the first hike since 2018. Yields started rapid ascent.", impact: "bullish", search: "Federal Reserve rate hike March 2022" },
    { date: "2023-10-19", title: "10Y Hits 5%", desc: "10-Year yield hit 5% for the first time since 2007 on persistent inflation fears.", impact: "bullish", search: "10 year treasury yield 5 percent 2023" },
    { date: "2024-09-18", title: "Fed First Rate Cut", desc: "Fed cut rates 50bp — first cut in 4 years. Yields began declining.", impact: "bearish", search: "Federal Reserve rate cut September 2024" },
    { date: "2025-04-04", title: "Yields Spike on Tariffs", desc: "10Y yield spiked as bond market sold off on fears of tariff-driven inflation.", impact: "bullish", search: "treasury yield spike tariff inflation April 2025" },
  ],
  gold: [
    { date: "2020-03-16", title: "COVID Gold Crash", desc: "Gold dropped to $1,471 as investors sold everything for USD cash.", impact: "bearish", search: "gold price crash COVID" },
    { date: "2020-08-06", title: "Gold ATH $2,075", desc: "Gold hit all-time high on unlimited Fed QE, zero rates, and inflation fears.", impact: "bullish", search: "gold all time high 2075 2020" },
    { date: "2022-03-08", title: "Ukraine War Spike", desc: "Gold surged to $2,070 as Russia invaded Ukraine, driving safe-haven demand.", impact: "bullish", search: "gold price Russia Ukraine war" },
    { date: "2022-11-03", title: "Gold Bottom $1,618", desc: "Gold bottomed as strong dollar and high rates pressured non-yielding assets.", impact: "bearish", search: "gold price bottom November 2022" },
    { date: "2024-03-08", title: "Gold Breaks $2,200", desc: "Gold surged on central bank buying and geopolitical tensions.", impact: "bullish", search: "gold price 2200 central bank buying" },
    { date: "2024-10-30", title: "Gold ATH $2,790", desc: "Gold hit new ATH on BRICS de-dollarization and US debt concerns.", impact: "bullish", search: "gold all time high 2790 2024" },
    { date: "2025-02-11", title: "Gold Breaks $2,900", desc: "Gold broke $2,900 on trade tensions and central bank buying.", impact: "bullish", search: "gold price 2900 February 2025" },
    { date: "2025-03-28", title: "Gold Tops $3,100", desc: "Gold surpassed $3,100 as tariff uncertainty and global recession fears drove safe-haven demand.", impact: "bullish", search: "gold price 3100 March 2025" },
    { date: "2025-04-03", title: "Gold Surges on Tariffs", desc: "Gold rallied as a safe haven after Trump's Liberation Day tariffs rocked global markets.", impact: "bullish", search: "gold tariff safe haven rally April 2025" },
  ],
};

function fmtPrice(p) {
  if (p >= 10000) return "$" + Math.round(p).toLocaleString();
  if (p >= 100) return "$" + p.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (p >= 1) return "$" + p.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return "$" + p.toFixed(4);
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-sm px-3 py-2 shadow-xl">
      <p className="text-[10px] text-zinc-500">{label}</p>
      <p className="text-sm font-data font-bold text-emerald-400">{fmtPrice(payload[0].value)}</p>
    </div>
  );
}

function EventDot({ cx, cy, impact, onClick }) {
  return (
    <g onClick={onClick} style={{ cursor: "pointer" }}>
      <circle cx={cx} cy={cy} r={10} fill="transparent" />
      <circle cx={cx} cy={cy} r={5} fill={impact === "bullish" ? "#22c55e" : "#ef4444"} stroke="#18181b" strokeWidth={2} />
    </g>
  );
}

function NewsPanel({ event, news, loading, onClose }) {
  if (!event) return null;
  return (
    <div className="bg-zinc-900/95 border border-zinc-700 rounded-sm shadow-2xl p-4 space-y-3" data-testid="news-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={`text-[8px] px-1.5 py-0.5 border-0 h-4 ${
              event.impact === "bullish" ? "bg-emerald-900/30 text-emerald-400" : "bg-red-900/30 text-red-400"
            }`}>
              {event.impact === "bullish" ? "BULLISH" : "BEARISH"}
            </Badge>
            <span className="text-[10px] font-data text-zinc-500">{event.date}</span>
            {event.price && <span className="text-[10px] font-data text-zinc-500">{fmtPrice(event.price)}</span>}
          </div>
          <h3 className="text-sm font-heading font-bold text-zinc-100 mt-1">{event.title}</h3>
          <p className="text-xs text-zinc-400 mt-1 leading-relaxed">{event.desc}</p>
        </div>
        <button onClick={onClose} className="text-zinc-600 hover:text-zinc-300 p-1 shrink-0" data-testid="close-news-panel">
          <X size={16} weight="bold" />
        </button>
      </div>

      <div className="border-t border-zinc-800/50 pt-3">
        <div className="flex items-center gap-1.5 mb-2">
          <Newspaper size={12} weight="bold" className="text-amber-400" />
          <span className="text-[10px] uppercase tracking-wider text-amber-400 font-bold">Headlines from this day</span>
          {loading && <Spinner size={10} className="animate-spin text-zinc-500 ml-1" />}
        </div>
        {news.length > 0 ? (
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {news.map((a, i) => (
              <a
                key={i}
                href={a.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`news-article-${i}`}
                className="block bg-zinc-950/60 border border-zinc-800/40 rounded-sm p-3 hover:border-zinc-700 transition-colors group"
              >
                <div className="flex items-start gap-3">
                  {a.image && <img src={a.image} alt="" className="w-14 h-10 object-cover rounded-sm shrink-0" onError={e => e.target.style.display='none'} />}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-zinc-200 group-hover:text-emerald-400 transition-colors leading-snug line-clamp-2">{a.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[9px] text-zinc-600">{a.source}</span>
                      <ArrowSquareOut size={9} className="text-zinc-700 group-hover:text-zinc-500" />
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        ) : (
          !loading && (
            <p className="text-[11px] text-zinc-600 italic">
              No archived headlines available for this date. NewsAPI free tier covers recent articles only.
            </p>
          )
        )}
      </div>
    </div>
  );
}

export default function HistoricData() {
  const [asset, setAsset] = useState("bitcoin");
  const [period, setPeriod] = useState("5y");
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [newsData, setNewsData] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [view, setView] = useState("events");

  useEffect(() => {
    fetchData();
    setSelectedEvent(null);
    setExpandedEvent(null);
    setNewsData([]);
  }, [asset, period]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(`${API}/historic-chart?asset=${asset}&period=${period}`);
      setChartData(resp.data.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load data");
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };

  const events = EVENTS[asset] || [];

  const visibleEvents = useMemo(() => {
    if (!chartData.length || !events.length) return [];
    const startDate = chartData[0].date;
    const endDate = chartData[chartData.length - 1].date;
    return events.filter(e => e.date >= startDate && e.date <= endDate);
  }, [chartData, events]);

  const eventWithPrices = useMemo(() => {
    if (!chartData.length) return visibleEvents;
    const dateMap = {};
    chartData.forEach(d => { dateMap[d.date] = d.price; });
    return visibleEvents.map(ev => {
      let price = dateMap[ev.date];
      if (!price) {
        const evDate = new Date(ev.date).getTime();
        let closest = null;
        let minDiff = Infinity;
        chartData.forEach(d => {
          const diff = Math.abs(new Date(d.date).getTime() - evDate);
          if (diff < minDiff) { minDiff = diff; closest = d; }
        });
        price = closest?.price;
      }
      return { ...ev, price };
    });
  }, [chartData, visibleEvents]);

  const fetchNews = useCallback(async (ev) => {
    setSelectedEvent(ev);
    setNewsLoading(true);
    setNewsData([]);
    try {
      const resp = await axios.get(`${API}/historic-event-news?query=${encodeURIComponent(ev.search || ev.title)}&date=${ev.date}`);
      setNewsData(resp.data.articles || []);
    } catch {
      setNewsData([]);
    } finally {
      setNewsLoading(false);
    }
  }, []);

  const handleEventClick = (ev) => {
    if (selectedEvent?.date === ev.date && selectedEvent?.title === ev.title) {
      setSelectedEvent(null);
      setNewsData([]);
    } else {
      fetchNews(ev);
    }
  };

  const assetInfo = ASSETS.find(a => a.id === asset);

  const tickFormatter = (val) => {
    if (!val) return "";
    const parts = val.split("-");
    return `${parts[1]}/${parts[0].slice(2)}`;
  };

  return (
    <div data-testid="historic-data-page">
      <div className="sticky top-0 z-20 bg-zinc-950/95 backdrop-blur-sm border-b border-zinc-800/60 px-5 py-4">
        <div className="flex items-center gap-2.5">
          <ClockCounterClockwise size={22} weight="bold" className="text-emerald-400" />
          <h1 className="font-heading text-2xl font-bold text-zinc-50 tracking-tight">Historic Data</h1>
          <Badge className="text-[8px] px-1.5 py-0.5 border-0 bg-emerald-900/30 text-emerald-400 h-4">EVENTS</Badge>
        </div>
        <div className="flex items-center gap-2 mt-2.5">
          <button
            data-testid="view-events-btn"
            onClick={() => setView("events")}
            className={`px-3 py-1.5 rounded-sm text-[11px] font-medium transition-colors border ${
              view === "events"
                ? "bg-zinc-800 text-zinc-100 border-emerald-700/50"
                : "bg-zinc-950/50 text-zinc-500 border-zinc-800/60 hover:text-zinc-300"
            }`}
          >
            Market Events
          </button>
          <button
            data-testid="view-astrology-btn"
            onClick={() => setView("astrology")}
            className={`px-3 py-1.5 rounded-sm text-[11px] font-medium transition-colors border flex items-center gap-1.5 ${
              view === "astrology"
                ? "bg-zinc-800 text-zinc-100 border-violet-700/50"
                : "bg-zinc-950/50 text-zinc-500 border-zinc-800/60 hover:text-zinc-300"
            }`}
          >
            <Planet size={12} weight="bold" />
            Astrology & Liquidity
          </button>
        </div>
      </div>

      {view === "astrology" ? (
        <div className="p-5 max-w-[1100px]">
          <AstrologyLiquidity />
        </div>
      ) : (
      <div className="p-5 space-y-5 max-w-[1100px]">

        {/* Asset + Period Selectors */}
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block font-medium">Select Asset</label>
            <div className="flex flex-wrap gap-1.5">
              {ASSETS.map(a => (
                <button
                  key={a.id}
                  data-testid={`asset-btn-${a.id}`}
                  onClick={() => setAsset(a.id)}
                  className={`px-3 py-1.5 rounded-sm text-[11px] transition-colors border ${
                    asset === a.id
                      ? "bg-zinc-800 text-zinc-100 border-emerald-700/50"
                      : "bg-zinc-950/50 text-zinc-500 border-zinc-800/60 hover:text-zinc-300 hover:border-zinc-700"
                  }`}
                >
                  {a.short}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block font-medium">Timeframe</label>
            <div className="flex gap-1">
              {PERIODS.map(p => (
                <button
                  key={p.id}
                  data-testid={`period-btn-${p.id}`}
                  onClick={() => setPeriod(p.id)}
                  className={`px-3 py-1.5 rounded-sm text-[11px] transition-colors border ${
                    period === p.id
                      ? "bg-zinc-800 text-zinc-100 border-emerald-700/50"
                      : "bg-zinc-950/50 text-zinc-500 border-zinc-800/60 hover:text-zinc-300 hover:border-zinc-700"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Chart */}
        <div className="bg-zinc-900/50 border border-zinc-800/80 rounded-sm p-4" data-testid="historic-chart">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-heading font-bold text-zinc-200">{assetInfo?.label} Price History</h2>
            <div className="flex items-center gap-2">
              <span className="text-[9px] text-zinc-600">Click event dots for headlines</span>
              {loading && <Spinner size={14} className="animate-spin text-zinc-500" />}
            </div>
          </div>

          {error && (
            <div className="bg-red-950/20 border border-red-800/30 rounded-sm p-3 mb-3">
              <p className="text-xs text-red-400">{error}</p>
            </div>
          )}

          {chartData.length > 0 ? (
            <div className="h-[350px] sm:h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
                  <defs>
                    <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 9, fill: "#71717a" }}
                    tickFormatter={tickFormatter}
                    interval={Math.floor(chartData.length / 8)}
                    axisLine={{ stroke: "#27272a" }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 9, fill: "#71717a" }}
                    tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}K` : `$${v}`}
                    axisLine={false}
                    tickLine={false}
                    width={55}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="price" stroke="#10b981" strokeWidth={1.5} fill="url(#priceGrad)" />
                  {/* Event reference lines + clickable dots */}
                  {eventWithPrices.map((ev, idx) => (
                    <ReferenceLine
                      key={`line-${idx}`}
                      x={ev.date}
                      stroke={ev.impact === "bullish" ? "#22c55e" : "#ef4444"}
                      strokeWidth={1}
                      strokeDasharray="3 3"
                      strokeOpacity={selectedEvent?.date === ev.date ? 0.9 : 0.35}
                    />
                  ))}
                  {eventWithPrices.map((ev, idx) => (
                    ev.price != null && (
                      <ReferenceDot
                        key={`dot-${idx}`}
                        x={ev.date}
                        y={ev.price}
                        shape={<EventDot impact={ev.impact} onClick={() => handleEventClick(ev)} />}
                      />
                    )
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            !loading && !error && (
              <div className="h-[300px] flex items-center justify-center">
                <p className="text-xs text-zinc-600">Select an asset to view its price history</p>
              </div>
            )
          )}
        </div>

        {/* News Panel (appears when event is selected) */}
        {selectedEvent && (
          <NewsPanel
            event={selectedEvent}
            news={newsData}
            loading={newsLoading}
            onClose={() => { setSelectedEvent(null); setNewsData([]); }}
          />
        )}

        {/* Events Timeline */}
        {eventWithPrices.length > 0 && (
          <div className="bg-zinc-900/50 border border-zinc-800/80 rounded-sm" data-testid="events-timeline">
            <div className="p-4 border-b border-zinc-800/50">
              <h3 className="text-sm font-heading font-bold text-zinc-100 flex items-center gap-2">
                <MapPin size={14} weight="bold" className="text-emerald-400" />
                Key Events Timeline
                <Badge className="text-[8px] px-1.5 py-0.5 border-0 bg-zinc-800 text-zinc-400 h-4 ml-1">{eventWithPrices.length}</Badge>
                <span className="text-[9px] text-zinc-600 font-normal ml-2">Click any event to see news</span>
              </h3>
            </div>
            <div className="divide-y divide-zinc-800/40">
              {eventWithPrices.map((ev, idx) => {
                const isSelected = selectedEvent?.date === ev.date && selectedEvent?.title === ev.title;
                return (
                  <button
                    key={idx}
                    data-testid={`event-${idx}`}
                    onClick={() => handleEventClick(ev)}
                    className={`w-full text-left px-4 py-3 hover:bg-zinc-800/20 transition-colors ${isSelected ? "bg-zinc-800/30 border-l-2 border-l-emerald-500" : ""}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${ev.impact === "bullish" ? "bg-emerald-500" : "bg-red-500"} ${isSelected ? "ring-2 ring-offset-1 ring-offset-zinc-900 ring-emerald-500/50" : ""}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] font-data text-zinc-600">{ev.date}</span>
                          <span className="text-xs font-heading font-bold text-zinc-200">{ev.title}</span>
                          {ev.price && <span className="text-[10px] font-data text-zinc-500 ml-auto shrink-0">{fmtPrice(ev.price)}</span>}
                        </div>
                      </div>
                      <Newspaper size={12} className={`shrink-0 ${isSelected ? "text-amber-400" : "text-zinc-700"}`} />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="flex items-center gap-4 text-[10px] text-zinc-600 flex-wrap">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Bullish Event</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500" /> Bearish Event</span>
          <span>Click dots on chart or events in timeline to see headlines</span>
        </div>
      </div>
      )}
    </div>
  );
}
